import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from '../services/api';

const AuthContext = createContext(null);

// Estados do contexto de autenticação
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { 
        ...state, 
        loading: true, 
        error: null 
      };
      
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        user: action.payload.user,
        error: null
      };
      
    case 'LOGOUT':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        error: null
      };
      
    case 'ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload
      };
      
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
      
    default:
      return state;
  }
};

// Estado inicial
const initialState = {
  loading: true,
  isAuthenticated: false,
  user: null,
  error: null
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Verificar token ao carregar aplicação
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        
        if (!token) {
          dispatch({ type: 'LOGOUT' });
          return;
        }

        // Verificar se token não está expirado
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          
          if (decoded.exp < currentTime) {
            console.log('Token expired, attempting refresh...');
            throw new Error('Token expired');
          }
        } catch (decodeError) {
          console.error('Token decode error:', decodeError);
          throw new Error('Invalid token');
        }

        // Buscar informações do usuário
        const response = await api.get('/auth/me');
        
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user: response.data }
        });
        
        console.log('✅ User authenticated:', response.data.name);
        
      } catch (error) {
        console.error('Auth initialization failed:', error);
        
        // Limpar tokens inválidos
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        
        dispatch({ type: 'LOGOUT' });
      }
    };

    initializeAuth();
  }, []);

  // Escutar evento de logout forçado
  useEffect(() => {
    const handleForcedLogout = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth:logout', handleForcedLogout);
    return () => window.removeEventListener('auth:logout', handleForcedLogout);
  }, []);

  // Função de login
  const login = async (email, password) => {
    dispatch({ type: 'LOADING' });
    
    try {
      console.log('🔐 Attempting login for:', email);
      console.log('🔗 API Base URL:', process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1');
      
      const response = await api.post('/auth/login', { 
        email: email.trim(), 
        password 
      });
      
      console.log('✅ Login response:', response.data);
      
      const { access_token, refresh_token, user_info } = response.data;
      console.log('🔑 Tokens received:', { 
        access_token: access_token ? 'YES' : 'NO', 
        refresh_token: refresh_token ? 'YES' : 'NO',
        user_info: user_info ? 'YES' : 'NO'
      });
      console.log('👤 User info:', user_info);

      if (!access_token || !refresh_token || !user_info) {
        console.error('❌ Missing data:', { access_token: !!access_token, refresh_token: !!refresh_token, user_info: !!user_info });
        throw new Error('Invalid response format from server');
      }

      // Salvar tokens
      localStorage.setItem('accessToken', access_token);
      localStorage.setItem('refreshToken', refresh_token);
      console.log('💾 Tokens saved to localStorage');

      // Atualizar estado
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: user_info }
      });
      console.log('🔄 Dispatch LOGIN_SUCCESS completed');

      console.log('✅ Login successful:', user_info.name);
      
      return { success: true, user: user_info };
      
    } catch (error) {
      console.error('❌ Login failed:', error);
      
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Erro no login. Verifique suas credenciais.';
      
      dispatch({ 
        type: 'ERROR', 
        payload: errorMessage 
      });
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  // Função de logout
  const logout = async () => {
    try {
      console.log('🚪 Logging out...');
      
      // Tentar fazer logout no servidor
      await api.post('/auth/logout');
      
    } catch (error) {
      console.error('Logout error (non-critical):', error);
    } finally {
      // Sempre limpar tokens e estado local
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      dispatch({ type: 'LOGOUT' });
      
      console.log('✅ Logout completed');
    }
  };

  // Verificar se usuário tem role específica
  const hasRole = (requiredRoles) => {
    if (!state.user || !Array.isArray(requiredRoles)) {
      return false;
    }
    
    return requiredRoles.includes(state.user.role);
  };

  // Verificar se usuário tem pelo menos uma das roles
  const hasAnyRole = (roles) => {
    if (!state.user || !Array.isArray(roles)) {
      return false;
    }
    
    return roles.some(role => state.user.role === role);
  };

  // Verificar se é admin ou superior
  const isAdmin = () => {
    return hasAnyRole(['admin', 'owner']);
  };

  // Verificar se é manager ou superior
  const isManager = () => {
    return hasAnyRole(['manager', 'admin', 'owner']);
  };

  // Limpar erro
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Refresh manual do usuário
  const refreshUser = async () => {
    if (!state.isAuthenticated) return;
    
    try {
      const response = await api.get('/auth/me');
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { user: response.data }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      throw error;
    }
  };

  const value = {
    // Estado
    ...state,
    
    // Ações
    login,
    logout,
    clearError,
    refreshUser,
    
    // Verificações de role
    hasRole,
    hasAnyRole,
    isAdmin,
    isManager,
    
    // Informações úteis
    userName: state.user?.name || '',
    userEmail: state.user?.email || '',
    userRole: state.user?.role || '',
    organizationName: state.user?.organization_name || ''
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook para usar o contexto
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

export default AuthContext;